import React from "react";

/**
 * Production-grade React error boundary.
 *
 * Catches uncaught render errors, resource errors and lifecycle errors in the
 * subtree and shows a branded fallback. Does NOT display stack traces to end
 * users — details go to console.error only (which the platform log collector
 * can pipe to Sentry/logs later).
 *
 * Deliberately independent from NotFound.jsx: a 404 is a routing miss, this
 * is an unexpected crash. Different tone and different CTAs.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError(_error) {
    // Short opaque id so the user can quote it to support if they call in.
    const errorId = "ERR-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    return { hasError: true, errorId };
  }

  componentDidCatch(error, info) {
    // Silent, structured log for the browser console — surfaces to platform
    // log collection but never renders to the user.
    console.error("[ErrorBoundary]", {
      id: this.state.errorId,
      name: error?.name,
      message: error?.message,
      component_stack: info?.componentStack,
      href: typeof window !== "undefined" ? window.location.href : "",
      at: new Date().toISOString(),
    });
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  handleHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main
        aria-labelledby="error-boundary-heading"
        aria-live="assertive"
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "100vh",
          background: "#000",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 24px",
          fontFamily: "-apple-system, Helvetica, Arial, sans-serif",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 560, width: "100%" }}>
          <div
            aria-hidden="true"
            style={{
              fontFamily: "'Bebas Neue', Impact, sans-serif",
              fontSize: "clamp(80px, 18vw, 160px)",
              lineHeight: 1,
              letterSpacing: "0.04em",
              color: "#E50914",
              textShadow: "0 0 60px rgba(229,9,20,0.35)",
              marginBottom: 16,
            }}
          >
            OOPS
          </div>
          <h1
            id="error-boundary-heading"
            style={{
              fontFamily: "'Bebas Neue', Impact, sans-serif",
              fontSize: "clamp(28px, 5vw, 44px)",
              letterSpacing: "0.02em",
              margin: "8px 0 16px",
            }}
          >
            Something went wrong
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.55, color: "#a1a1aa", margin: "0 0 32px" }}>
            Please refresh the page or contact OzyLoans. If this keeps happening, quote
            reference <strong style={{ color: "#fff" }} data-testid="error-boundary-ref">
              {this.state.errorId || "—"}
            </strong> when you get in touch.
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              justifyContent: "center",
              marginBottom: 40,
            }}
          >
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-refresh"
              style={{
                background: "#E50914",
                color: "#fff",
                border: 0,
                borderRadius: 999,
                padding: "12px 24px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Refresh page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              data-testid="error-boundary-home"
              style={{
                background: "transparent",
                color: "#fff",
                border: "1px solid #3f3f46",
                borderRadius: 999,
                padding: "12px 24px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Return home
            </button>
            <a
              href="tel:+61426976876"
              data-testid="error-boundary-call"
              style={{
                background: "transparent",
                color: "#e4e4e7",
                border: "1px solid #3f3f46",
                borderRadius: 999,
                padding: "12px 24px",
                fontWeight: 600,
                fontSize: 14,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Call an adviser
            </a>
          </div>

          <div style={{ borderTop: "1px solid #18181b", paddingTop: 24, fontSize: 12, color: "#52525b" }}>
            OzyLoans · ACL #485752 · AFCA #618333 · MFAA #246073
          </div>
        </div>
      </main>
    );
  }
}
